import { convertToArray } from "@/helpers/common";
import {
  BOOLEAN_FILTERS,
  DATE_RANGE_FILTERS,
  NUMBER_ARRAY_FILTERS,
  QUERY_KEY_TO_FILTER_KEY,
  STRING_ARRAY_FILTERS,
  WORKFLOW_FILTERS,
} from "@/helpers/constants/filterTypes";

import type { DealsFilterValue, ISmartFilterData } from "@/models/applications";
import type { LocationQuery, LocationQueryValue } from "vue-router";

export const filterHasValue = (
  value:
    | DealsFilterValue
    | string
    | number
    | string[]
    | number[]
    | null
    | undefined
) =>
  Array.isArray(value) ? !!value.length : !!value || Number.isInteger(value);

export const parseQueryToFilterData = (
  query: LocationQuery
): Partial<ISmartFilterData> => {
  const filterData: Partial<ISmartFilterData> = {};

  const toStringArray = (
    value: LocationQueryValue | LocationQueryValue[]
  ): string[] | null => {
    if (!value) {
      return null;
    }

    const values = convertToArray(value);
    const filtered = values.filter((v): v is string => v !== null);
    return filtered.length ? filtered : null;
  };

  const toNumberArray = (stringArr: string[] | null): number[] | null => {
    if (!stringArr) {
      return null;
    }
    const numbers = stringArr.map(Number).filter((n) => !isNaN(n));
    return numbers.length ? numbers : null;
  };

  const toNumber = (
    value: LocationQueryValue | LocationQueryValue[]
  ): number | null => {
    if (!value) {
      return null;
    }
    const val = Array.isArray(value) ? value[0] : value;
    if (!val) {
      return null;
    }
    const num = Number(val);
    return isNaN(num) ? null : num;
  };

  const assignFilter = (
    key: string,
    value: string[] | number[] | number | null
  ) => {
    if (value === null || (Array.isArray(value) && !value.length)) {
      return;
    }

    (filterData as Record<string, string[] | number[] | number>)[key] = value;
  };

  Object.keys(query).forEach((queryKey) => {
    const rawKey = queryKey.replace(/\[\]$/, "");
    const key = QUERY_KEY_TO_FILTER_KEY[rawKey] || rawKey;
    const value = query[queryKey];

    if (!filterHasValue(value as string | string[] | number | number[])) {
      return;
    }

    if (key in WORKFLOW_FILTERS) {
      const num = toNumber(value);
      if (num === 1) {
        filterData.workflows = [
          ...(filterData.workflows ?? []),
          WORKFLOW_FILTERS[key],
        ];
      }
      return;
    }

    if (STRING_ARRAY_FILTERS.has(key) || DATE_RANGE_FILTERS.has(key)) {
      assignFilter(key, toStringArray(value));
      return;
    }

    if (NUMBER_ARRAY_FILTERS.has(key)) {
      const stringArr = toStringArray(value);
      assignFilter(key, toNumberArray(stringArr));
      return;
    }

    if (BOOLEAN_FILTERS.has(key)) {
      assignFilter(key, toNumber(value));
    }
  });

  return filterData;
};
